import type { PrepareAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';
import merge from 'deepmerge';
import _get from 'lodash/get';
import _isPlainObject from 'lodash/isPlainObject';
import _set from 'lodash/set';
import { useCallback } from 'react';
import type { PartialDeep } from 'type-fest';

import type {
  ArtifactVulnerabilitiesFilters,
  NestedViewState,
  ViewStatePath,
  ViewTypes,
} from '@models';
import { initialTableViewProps } from '@models';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { VulnAnnotationStatus } from '@features/artifact/artifacts.models';

export type ViewsState = ViewTypes;

export const initialArtifactVulnerabilitiesFiltersState: ArtifactVulnerabilitiesFilters =
  {
    annotationStatus: [
      'None',
      VulnAnnotationStatus.UnderInvestigation,
      VulnAnnotationStatus.Affected,
    ],
    isVendorDisputed: false,
  };

export const initialState: ViewsState = {
  artifactAnalysis: {
    vulnerabilities: {
      filters: { ...initialArtifactVulnerabilitiesFiltersState },
      pagination: { ...initialTableViewProps },
    },
  },
  gettingStarted: {},
  inventoriesClustersTable: {
    ...initialTableViewProps,
    sort: { id: 'name', desc: true },
  },
  inventoriesNamespacesTable: {
    ...initialTableViewProps,
    sort: { id: 'imageCountNamespaces', desc: true },
  },
  inventoriesImagesTable: {
    ...initialTableViewProps,
    sort: { lastAnalyzed: true },
  },
  inventoriesVulnerabilitiesTable: {
    ...initialTableViewProps,
    sort: { id: 'severity', desc: true },
  },
  inventoriesViewFilters: {
    compliance: {
      non_compliant_images: true,
      compliant_images: true,
      not_evaluated_images: true,
    },
    vulnerabilities: {
      critical: true,
      high: true,
      medium: true,
      low: true,
      negligible: true,
      unknown: true,
    },
  },
  sbomAnalysis: {
    compliance: {
      pagination: {
        ...initialTableViewProps,
        sort: {
          action: true,
        },
      },
      filters: {
        allowlisted: false,
      },
      problems: {
        pagination: {
          ...initialTableViewProps,
          pageSize: 5,
          sort: { severity: true },
        },
      },
    },
    packages: {
      pagination: {
        ...initialTableViewProps,
      },
    },
    vulnerabilities: {
      pagination: {
        ...initialTableViewProps,
        sort: {},
      },
      filters: {
        lastFound: '24h',
      },
    },
  },
  sboms: {
    pagination: {
      ...initialTableViewProps,
      page: 0,
      sort: { updatedAt: true },
    },
    filters: {
      groups: [],
    },
    byGroup: {
      pagination: {
        ...initialTableViewProps,
        page: 0,
        sort: { updatedAt: true },
      },
      filters: {},
    },
  },
  systemIntegrationsList: initialTableViewProps,
};

export interface SetViewStateOptions {
  isDeepMerge?: boolean;
  overwrite?: boolean;
}

type ViewStatePrepareAction<Path extends ViewStatePath = ViewStatePath> =
  PrepareAction<{
    path: Path;
    state: PartialDeep<NestedViewState<Path>>;
    options?: SetViewStateOptions;
  }>;

/**
 * @internal
 *
 * This action definition is used for the slice implementation, but lacks the
 * generic typing we need for dispatchers, so is wrapped into the `setViewState`
 * export.
 *
 * @see setViewState
 */
const setViewStateAction = createAction<ViewStatePrepareAction>(
  'views/setViewState',
  <Path extends ViewStatePath>(
    path: Path,
    state: PartialDeep<NestedViewState<Path>>,
    options?: SetViewStateOptions,
  ) => ({ payload: { path, state, options } }),
);

/**
 * Set view state at any depth by dot-notation path.
 *
 * Objects are treated as partials to be shallow merged with existing state,
 * allowing nested objects to be overwritten if necessary. Also supports deep
 * merge via options.
 *
 * @param path - Dot-notation path to the target view state
 * @param state - Partial state to merge/overwrite at the target path
 * @param options - Optional settings, e.g. merge strategy
 */
export const setViewState = <Path extends ViewStatePath>(
  path: Path,
  state: PartialDeep<NestedViewState<Path>>,
  options?: SetViewStateOptions,
) =>
  // Just a wrapper for the underlying action to provide strict type inference
  setViewStateAction(path, state, options);

const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(setViewStateAction, (state, { payload }) => {
      const isDeepMerge = payload.options?.isDeepMerge ?? false;
      const overwrite = payload.options?.overwrite ?? false;

      // Non-objects will overwrite existing state
      let nextState = payload.state;

      // Always treat objects as partials to be merged, unless overwrite is true
      if (_isPlainObject(nextState) && !overwrite) {
        type NextState = typeof nextState;
        const currentState = _get(state, payload.path) as NextState;

        nextState = isDeepMerge
          ? // deepmerge is optional to allow more granular control by default
            merge<NextState>(currentState, nextState, {
              // Don't merge arrays, just overwrite
              arrayMerge: (_, source) => source,
            })
          : // Otherwise shallow merge to support nested object overwrites
            Object.assign(currentState ?? {}, nextState);
      }

      _set(state, payload.path, nextState);
    });
  },
});

export const useGetViewState = <Path extends ViewStatePath>(
  path: Path,
): NestedViewState<Path> =>
  useAppSelector(state => _get(state.views, path) as NestedViewState<Path>);

export const useSetViewState = <Path extends ViewStatePath>(
  path: Path,
  initialOptions?: SetViewStateOptions,
) => {
  const dispatch = useAppDispatch();

  const updateViewState = useCallback(
    (
      state: PartialDeep<NestedViewState<Path>>,
      options?: SetViewStateOptions,
    ) => {
      dispatch(setViewState(path, state, options ?? initialOptions));
    },
    [dispatch, path, initialOptions],
  );

  return { updateViewState };
};

export const useViewState = <Path extends ViewStatePath>(path: Path) => {
  const state = useGetViewState(path);
  const mutations = useSetViewState(path);

  return [state, mutations] as const;
};

export default viewsSlice.reducer;
